import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { ticketsApi } from '@/shared/api/tickets';
import { apiErrorText, supportGroupsApi, type SupportGroup, type UserSearchResult } from '@/shared/api';
import type { TicketAssignee } from '@/entities/ticket';
import {
  AsyncUserSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Field,
  Select,
  toast,
} from '@/shared/ui';
import { getUserDisplayName } from './lib';
import styles from './TicketDetail.module.css';

interface ReassignAssigneeDialogProps {
  ticketId: string;
  assignee: TicketAssignee | null;
  canReassignGroup: boolean;
  creatorId?: string | null;
  onClose: () => void;
  onDone: () => void;
}

export const ReassignAssigneeDialog: React.FC<ReassignAssigneeDialogProps> = ({
  ticketId,
  assignee,
  canReassignGroup,
  creatorId,
  onClose,
  onDone,
}) => {
  const [target, setTarget] = useState<UserSearchResult | null>(null);
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (assignee) {
      setTarget(null);
      setGroupId('');
    }
  }, [assignee]);

  useEffect(() => {
    if (!canReassignGroup) return;
    supportGroupsApi
      .listBrief()
      .then((res) => setGroups(res.data ?? []))
      .catch(() => setGroups([]));
  }, [canReassignGroup]);

  const handleSubmit = async () => {
    if (!assignee || !target) return;
    setSaving(true);
    try {
      await ticketsApi.reassignAssignee(ticketId, assignee.userId, target.id, groupId || undefined);
      toast.success(`Заявка переназначена: ${target.displayName}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось переназначить'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={assignee != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogTitle>Переназначить ответственного</DialogTitle>
        <DialogDescription>
          Текущий исполнитель будет снят, новый — назначен. Если новый исполнитель
          из другой группы, заявка перейдёт в его группу.
        </DialogDescription>
        <div className={styles.reassignForm}>
          {assignee && (
            <div className={styles.reassignSwap}>
              <span className={styles.reassignFrom}>
                {getUserDisplayName(assignee.user, assignee.userId)}
              </span>
              <ArrowRight size={14} aria-hidden className={styles.reassignArrow} />
              <span className={target ? styles.reassignTo : styles.reassignToEmpty}>
                {target ? target.displayName : 'не выбран'}
              </span>
            </div>
          )}
          <AsyncUserSelect
            placeholder="На кого переназначить…"
            onSelect={setTarget}
            disabled={saving}
            staffOnly
            excludeUserIds={creatorId ? [creatorId] : undefined}
            excludeReason="автор заявки"
          />
          {canReassignGroup && groups.length > 0 && (
            <Field label="Группа">
              <Select
                value={groupId}
                onValueChange={setGroupId}
                disabled={saving}
                options={[
                  { value: '', label: 'Авто (по группе нового исполнителя)' },
                  ...groups.map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={!target || saving}>
            Переназначить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
